import * as React from "react";
import * as s from "./ListViewCard.style.css";
import type { ListViewCTAGroup, ListViewCardLayout } from "./ListViewCard.types";
import { useT } from "../../../../../app/lib/i18n";

interface CardCtaOverlayProps {
  groups: ListViewCTAGroup[];
  layout: ListViewCardLayout;
}

const ANIM_MS = 160;

export function CardCtaOverlay({
  groups, layout }: CardCtaOverlayProps) {
  const t = useT();
  const [ctaIndex, setCtaIndex] = React.useState(0);
  const [shownIndex, setShownIndex] = React.useState(0);
  const [phase, setPhase] = React.useState<"idle" | "exit" | "enter">("idle");
  const targetRef = React.useRef(0);
  const timersRef = React.useRef<ReturnType<typeof setTimeout>[]>([]);

  const groupIds = groups.map((g) => g.id).join("\0");
  const prevGroupIdsRef = React.useRef(groupIds);
  if (prevGroupIdsRef.current !== groupIds) {
    prevGroupIdsRef.current = groupIds;
    if (ctaIndex !== 0) setCtaIndex(0);
    setShownIndex(0);
    setPhase("idle");
    timersRef.current.forEach(clearTimeout);
    timersRef.current = [];
  }

  // Trigger slide transition whenever ctaIndex changes
  React.useEffect(() => {
    if (ctaIndex === shownIndex) return;
    timersRef.current.forEach(clearTimeout);
    timersRef.current = [];
    targetRef.current = ctaIndex;
    setPhase("exit");
    const t1 = setTimeout(() => {
      setShownIndex(targetRef.current);
      setPhase("enter");
      const t2 = setTimeout(() => setPhase("idle"), ANIM_MS);
      timersRef.current = [t2];
    }, ANIM_MS);
    timersRef.current = [t1];
    return () => { timersRef.current.forEach(clearTimeout); timersRef.current = []; };
  }, [ctaIndex]);

  const groupCount = groups.length;
  const group = groups[shownIndex];
  if (!group) return null;

  const animClass = phase === "exit" ? s.ctaContentExit : phase === "enter" ? s.ctaContentEnter : undefined;

  const advanceCta = (e: React.MouseEvent | React.KeyboardEvent) => {
    e.stopPropagation();
    setCtaIndex((i) => (i + 1) % groupCount);
  };

  const ctaNavigator = groupCount > 1 ? (
    <div className={s.ctaNavigator} onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
      <button
        type="button"
        className={s.ctaNavigatorChip}
        onClick={advanceCta}
        aria-label={t("shared.listView.nextCta")}
      >
        {ctaIndex + 1}/{groupCount} ›
      </button>
    </div>
  ) : null;

  const ctaImageEl = group.image ? (
    <div
      className={s.ctaGroupImage}
      title={group.image.tooltip}
      onClick={group.image.onClick ? (e) => { e.stopPropagation(); group.image!.onClick!(); } : undefined}
      onKeyDown={group.image.onClick ? (e) => { if (e.key === "Enter" || e.key === " ") { e.stopPropagation(); group.image!.onClick!(); } } : undefined}
      style={group.image.onClick ? { cursor: "pointer" } : undefined}
      role={group.image.onClick ? "button" : undefined}
      tabIndex={group.image.onClick ? 0 : undefined}
    >
      {group.image.src ? (
        <img src={group.image.src} alt={group.image.tooltip ?? ""} style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
      ) : null}
    </div>
  ) : null;

  const ctaButtonEls = group.buttons?.map((cta) => (
    <button
      key={cta.id}
      type="button"
      className={getCtaBtnClass(cta.variant)}
      style={cta.color ? { background: cta.color, color: "#fff" } : undefined}
      disabled={cta.disabled}
      onClick={(e) => { e.stopPropagation(); cta.onClick(); }}
    >
      {cta.icon}
      {cta.label}
    </button>
  ));

  const ctaContentDeck = (
    <div style={{ display: "flex", flexDirection: "column", gap: 10, alignItems: "center", justifyContent: "center" }}>
      {(ctaImageEl || group.text) && (
        <div style={{ display: "flex", flexDirection: "row", gap: 6, alignItems: "center", justifyContent: "center", minHeight: 20 }}>
          {ctaImageEl}
          {group.text && <span className={s.ctaLabel}>{group.text}</span>}
        </div>
      )}
      {ctaButtonEls?.length ? (
        <div style={{ display: "flex", flexDirection: "row", gap: 6, alignItems: "center", justifyContent: "center" }}>
          {ctaButtonEls}
        </div>
      ) : null}
    </div>
  );

  const ctaContentStack = (
    <div style={{ display: "flex", flexDirection: "row", gap: 6, alignItems: "center" }}>
      {ctaImageEl}
      {group.text && <span className={s.ctaLabel}>{group.text}</span>}
      {ctaButtonEls}
    </div>
  );

  const innerBarClass = layout === "deck" ? s.ctaBarInnerDeck : s.ctaBarInner;
  const soloBarClass = layout === "deck" ? s.ctaOverlayDeck : s.ctaOverlay;

  if (groupCount > 1) {
    return (
      <div
        className={s.ctaOuterWrap}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
      >
        {ctaNavigator}
        <div className={innerBarClass}>
          <div className={animClass}>
            {layout === "deck" ? ctaContentDeck : ctaContentStack}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={soloBarClass}
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
    >
      <div className={animClass}>
        {layout === "deck" ? ctaContentDeck : ctaContentStack}
      </div>
    </div>
  );
}

function getCtaBtnClass(variant?: string): string {
  switch (variant) {
    case "danger": return s.ctaBtnDanger;
    case "secondary": return s.ctaBtnSecondary;
    default: return s.ctaBtnPrimary;
  }
}
